/**
 * Passport · Section C — Planning & Future Build
 */
import React from 'react';
import { Map, FileText, Home, Building, AlertCircle } from 'lucide-react';
import { Badge } from '../ui/badge';

const STATUS_TONE = {
  'Open for objection':       'bg-amber-100 text-amber-900 border-amber-200',
  'Further info requested':   'bg-stone-100 text-stone-600 border-stone-200',
  'Granted':                  'bg-emerald-100 text-emerald-800 border-emerald-200',
  'Refused':                  'bg-red-100 text-red-800 border-red-200',
  'Appealed to An Bord Pleanála': 'bg-purple-100 text-purple-800 border-purple-200',
};

const fmtDate = (iso, locale) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(locale || 'en-IE', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

const PlanningFutureSection = ({ section }) => {
  const d = section.details || {};
  const apps = d.applications || [];
  const zoning = d.zoning;
  const granny = d.granny_flat || {};
  const isUK = section.country_label === 'UK';
  const $ = section.currency_symbol || '€';
  const locale = section.locale || 'en-IE';

  return (
    <div className="space-y-4 pt-2">
      {/* Top row — zoning + granny */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {zoning && (
          <div className="rounded-lg border border-stone-200 bg-white p-4" data-testid="passport-zoning-card">
            <div className="flex items-center gap-2 mb-2">
              <Map className="h-4 w-4 text-purple-700" />
              <span className="font-semibold text-stone-900">Zoning</span>
            </div>
            <div className="text-sm text-stone-900 font-semibold">{zoning.label}</div>
            <div className="text-xs text-stone-600 mt-1">Max storeys (typical): {zoning.max_storeys}</div>
            {zoning.protected_structure_nearby && (
              <Badge className="bg-amber-100 text-amber-900 border-amber-200 text-[10px] py-0 mt-2">
                Protected structure nearby
              </Badge>
            )}
            <div className="text-[10px] text-stone-400 mt-2">Source: {isUK ? 'gov.uk / Local Plan' : 'LA Development Plan via Geohive'}.</div>
          </div>
        )}

        <div
          className={`rounded-lg border p-4 ${granny.eligible ? 'border-emerald-200 bg-emerald-50/40' : 'border-stone-200 bg-white'}`}
          data-testid="passport-granny-card"
        >
          <div className="flex items-center gap-2 mb-2">
            <Home className="h-4 w-4 text-purple-700" />
            <span className="font-semibold text-stone-900">{isUK ? 'Add-on potential' : 'Granny-flat / coach-house'}</span>
            {granny.eligible
              ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] py-0">Eligible</Badge>
              : <Badge variant="outline" className="text-[10px] py-0 text-stone-500">Site too small</Badge>}
          </div>
          {granny.eligible ? (
            <div className="text-sm text-stone-700 space-y-1">
              <div>Up to <strong>{granny.max_sqm} m²</strong> {isUK ? 'under' : 'annex exempted under'} {granny.scheme}.</div>
              <div className="text-xs text-stone-600">
                Build cost ≈ <strong>{$}{granny.build_cost_estimate_eur?.toLocaleString()}</strong> ·
                Resale uplift ≈ <strong className="text-emerald-700">+{$}{granny.uplift_estimate_eur?.toLocaleString()}</strong>
              </div>
              {granny.reason && (
                <div className="text-[11px] text-stone-500 mt-1">{granny.reason}</div>
              )}
            </div>
          ) : (
            <div className="text-xs text-stone-600">{granny.reason}</div>
          )}
          <div className="text-[10px] text-stone-400 mt-2">{isUK ? 'GPDO 2015 (as amended). Article 4 directions may apply.' : 'Indicative — exact eligibility uses OSi parcel boundary.'}</div>
        </div>
      </div>

      {/* Live planning apps list */}
      <div className="rounded-lg border border-stone-200 bg-white p-4" data-testid="passport-apps-card">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="h-4 w-4 text-purple-700" />
          <span className="font-semibold text-stone-900">Planning applications within 250 m</span>
          <Badge variant="outline" className="text-[10px] py-0">
            {apps.length} active
          </Badge>
        </div>
        {apps.length === 0 ? (
          <div className="text-sm text-stone-500 italic">No live applications inside the 250 m radius — quiet stretch.</div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {apps.map((a) => {
              const tone = STATUS_TONE[a.status] || 'bg-stone-100 text-stone-600 border-stone-200';
              return (
                <li key={a.reference} className="py-2.5 first:pt-0 last:pb-0" data-testid={`passport-app-${a.reference.replace(/[^\w]+/g, '_')}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-stone-900">{a.address}</span>
                        <Badge className={`${tone} text-[10px] py-0`}>{a.status}</Badge>
                      </div>
                      <div className="text-xs text-stone-700 mt-0.5">{a.summary}</div>
                      <div className="text-[11px] text-stone-500 mt-1">
                        Ref {a.reference} · {a.distance_m} m away · lodged {fmtDate(a.lodged, locale)} · decision due {fmtDate(a.decision_due, locale)}
                      </div>
                    </div>
                    <Building className="h-4 w-4 text-stone-300 flex-shrink-0 mt-1" />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <div className="text-[10px] text-stone-400 mt-2">
          {isUK
            ? 'Aggregated from gov.uk find-planning-applications + local authority portals.'
            : 'Aggregated from LA planning portals + the National Planning Application Database.'}
        </div>
      </div>

      {/* Helpful nudge — only when there's a live objection window */}
      {apps.some((a) => a.status === 'Open for objection') && (
        <div className="rounded-md border border-amber-200 bg-amber-50/50 p-3 flex items-start gap-2 text-xs" data-testid="passport-objection-nudge">
          <AlertCircle className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-stone-800">
            <strong>Heads-up:</strong> at least one application near this property is still open for public objection.
            {isUK
              ? ' Comments are submitted on the council\'s online planning portal.'
              : ' Email submissions to the council go to the planning department of the relevant Local Authority.'}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanningFutureSection;
